import {
  Component,
  EventEmitter,
  Injector,
  Input,
  OnInit,
  Output,
  forwardRef,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { RRIInputOption } from '../../interfaces/input-option.interface';
import { RRIInput } from '../../interfaces/input-settings.interface';

@Component({
  selector: 'rr-multi-select-field',
  templateUrl: './multi-select-field.component.html',
  styleUrls: ['./multi-select-field.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiSelectFieldComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => MultiSelectFieldComponent),
      multi: true,
    },
  ],
})
export class MultiSelectFieldComponent<T>
  implements OnInit, ControlValueAccessor, Validator
{
  public data: RRIInputOption<T>[];
  public onChangeFn: any = (_: any) => {};
  public onTouchFn: any = () => {};
  public control: NgControl;
  public noResult = false;
  private errorMessages = new Map<string, () => string>();

  @Input() settings: RRIInput<RRIInputOption<T>[]>;
  @Input() options: RRIInputOption<T>[];
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() customErrorMessages: Map<string, () => string>;
  @Input() allowCustomInput: boolean = false;
  @Output() clearInput: EventEmitter<boolean> = new EventEmitter();
  @Output() valueChanged: EventEmitter<RRIInputOption<T>[]> =
    new EventEmitter();

  constructor(private inj: Injector) {
    this.data = [];
    this.errorMessages.set(
      'required',
      () => `${this.settings.label} is required.`,
    );
  }

  ngOnInit(): void {
    this.control = this.inj.get(NgControl, null);

    if (this.customErrorMessages && this.customErrorMessages.size) {
      this.errorMessages = new Map([
        ...Array.from(this.errorMessages.entries()),
        ...Array.from(this.customErrorMessages.entries()),
      ]);
    }
  }

  public onValueChange(event: { id: number | string; value: T }[]): void {
    this.data = event;
    this.valueChanged.emit(this.data);
    this.onChange();
  }

  public changeOnNoResults(event) {
    this.noResult = event;
  }

  public onClearInput(): void {
    if (!this.settings.disableClearEvent) {
      if (this.settings.defaultValue) {
        this.data = this.settings.defaultValue;
      } else {
        this.data = [];
      }
    }

    this.clearInput.emit(true);
    this.onChange();
  }

  public compareWith(a: any, b: any): boolean {
    return a.id === b.id;
  }

  public writeValue(value: RRIInputOption<T>[]): void {
    this.data = value;
  }

  public registerOnChange(fn: RRIInputOption<T>[]): void {
    this.onChangeFn = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouchFn = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public onChange() {
    this.onChangeFn(this.data);
  }

  public get invalid(): boolean {
    return this.control ? this.control.invalid : false;
  }

  public get showError(): boolean {
    if (!this.control) {
      return false;
    }

    const { dirty, touched, value } = this.control;

    return this.invalid ? dirty || touched || !!value : false;
  }

  public get errors(): Array<string> {
    if (!this.control) {
      return [];
    }

    const { errors } = this.control;
    return Object.keys(errors).map((key) => {
      return this.errorMessages.has(key)
        ? this.errorMessages.get(key)()
        : 'error: ' + key;
    });
  }

  public validate(_control: AbstractControl): ValidationErrors | null {
    return null;
  }
}
